import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Plus, Ticket, RefreshCw, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { useAuth } from "@/hooks/useAuth";
import { useAdmin } from "@/hooks/useAdmin";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface StripeCoupon {
  id: string;
  name: string;
  percent_off: number | null;
  amount_off: number | null;
  currency: string | null;
  duration: string;
  max_redemptions: number | null;
  times_redeemed: number;
  valid: boolean;
  created: number;
}

const AdminCoupons = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: adminLoading } = useAdmin();
  const [coupons, setCoupons] = useState<StripeCoupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // New coupon form
  const [newName, setNewName] = useState("");
  const [newType, setNewType] = useState("percentage");
  const [newValue, setNewValue] = useState("");
  const [newMaxUses, setNewMaxUses] = useState("");

  useEffect(() => {
    if (!authLoading && !adminLoading) {
      if (!user) navigate("/login");
      else if (!isAdmin) navigate("/dashboard");
    }
  }, [user, isAdmin, authLoading, adminLoading, navigate]);

  const getAuthHeaders = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return { Authorization: `Bearer ${session?.access_token}` };
  };

  const fetchCoupons = async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const res = await supabase.functions.invoke("list-stripe-coupons", { headers, method: "GET" });
      if (res.error) throw res.error;
      setCoupons(res.data.coupons || []);
    } catch (err: any) {
      toast({ title: "Error loading coupons", description: err.message, variant: "destructive" });
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isAdmin) fetchCoupons();
  }, [isAdmin]);

  const createCoupon = async () => {
    if (!newName.trim() || !newValue) {
      toast({ title: "Missing fields", description: "Name and discount value are required.", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      const headers = await getAuthHeaders();
      const body: any = {
        name: newName.toUpperCase().trim(),
        duration: "once",
      };

      if (newType === "percentage") {
        body.percent_off = Number(newValue);
      } else if (newType === "fixed") {
        body.amount_off = Math.round(Number(newValue) * 100); // Stripe expects cents
        body.currency = "usd";
      } else if (newType === "free_essentials") {
        body.percent_off = 100;
      } else if (newType === "free_premium") {
        body.percent_off = 100;
      }

      if (newMaxUses) body.max_redemptions = Number(newMaxUses);

      const res = await supabase.functions.invoke("list-stripe-coupons", {
        headers,
        method: "POST",
        body,
      });

      if (res.error) throw res.error;
      if (res.data.error) throw new Error(res.data.error);

      toast({ title: "Coupon created in Stripe!" });
      setDialogOpen(false);
      setNewName("");
      setNewType("percentage");
      setNewValue("");
      setNewMaxUses("");
      fetchCoupons();
    } catch (err: any) {
      toast({ title: "Error creating coupon", description: err.message, variant: "destructive" });
    }
    setSaving(false);
  };

  const deleteCoupon = async (couponId: string) => {
    try {
      const headers = await getAuthHeaders();
      const res = await supabase.functions.invoke("list-stripe-coupons", {
        headers,
        body: { action: "delete", coupon_id: couponId },
      });
      if (res.error) throw res.error;
      if (res.data.error) throw new Error(res.data.error);
      toast({ title: "Coupon deleted from Stripe" });
      setCoupons(prev => prev.filter(c => c.id !== couponId));
    } catch (err: any) {
      toast({ title: "Error deleting coupon", description: err.message, variant: "destructive" });
    }
    setDeletingId(null);
  };

  // Plan restriction map
  const planRestriction: Record<string, string> = {
    omnGKE9X: "Premium",
    sXdxwYhW: "Premium",
    QCDq4aPs: "Essentials",
    NXs7dbMV: "Essentials",
    bwWwwez9: "Essentials",
    sofuR8Wt: "Essentials",
    "3mnWM4Uz": "Essentials",
    LE8bxNPo: "Essentials",
    O2VUCM6M: "Essentials",
    RJpNNuHT: "Essentials",
  };

  if (authLoading || adminLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <Button variant="ghost" size="sm" className="mb-4" onClick={() => navigate("/admin")}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-2">Coupon Codes</h1>
              <p className="text-muted-foreground">Your Stripe coupons and redemption status.</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={fetchCoupons}>
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh
              </Button>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button><Plus className="w-4 h-4 mr-2" /> New Coupon</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create Stripe Coupon</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-2">
                    <div>
                      <Label htmlFor="coupon-name">Coupon Name / Code</Label>
                      <Input id="coupon-name" value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. WELCOME20" className="uppercase" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="coupon-type">Type</Label>
                        <Select value={newType} onValueChange={(val) => {
                          setNewType(val);
                          if (val === "free_essentials" || val === "free_premium") setNewValue("100");
                          else setNewValue("");
                        }}>
                          <SelectTrigger id="coupon-type"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="percentage">Percentage (%)</SelectItem>
                            <SelectItem value="fixed">Fixed ($)</SelectItem>
                            <SelectItem value="free_essentials">Free Essentials (100%)</SelectItem>
                            <SelectItem value="free_premium">Free Premium (100%)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {newType !== "free_essentials" && newType !== "free_premium" ? (
                        <div>
                          <Label htmlFor="coupon-value">Value</Label>
                          <Input id="coupon-value" type="number" value={newValue} onChange={e => setNewValue(e.target.value)} placeholder={newType === "percentage" ? "20" : "10.00"} />
                        </div>
                      ) : (
                        <div>
                          <Label htmlFor="coupon-value-disabled">Value</Label>
                          <Input id="coupon-value-disabled" type="number" value="100" disabled className="bg-muted" />
                        </div>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="coupon-max-uses">Max Redemptions (blank = unlimited)</Label>
                      <Input id="coupon-max-uses" type="number" value={newMaxUses} onChange={e => setNewMaxUses(e.target.value)} placeholder="e.g. 1" />
                    </div>
                    <Button onClick={createCoupon} disabled={saving} className="w-full">
                      {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Create Coupon
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {coupons.length === 0 ? (
            <div className="bg-card rounded-2xl shadow-soft p-12 text-center">
              <Ticket className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No coupons found in Stripe.</p>
            </div>
          ) : (
            <div className="bg-card rounded-2xl shadow-soft overflow-hidden divide-y divide-border">
              {coupons.map(coupon => {
                const remaining = coupon.max_redemptions != null
                  ? coupon.max_redemptions - coupon.times_redeemed
                  : null;
                const plan = planRestriction[coupon.id];

                return (
                  <div key={coupon.id} className="p-5 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${coupon.valid ? "bg-primary/10" : "bg-muted"}`}>
                        <Ticket className={`w-5 h-5 ${coupon.valid ? "text-primary" : "text-muted-foreground"}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono font-bold text-foreground">{coupon.name}</span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                            {coupon.percent_off
                              ? `${coupon.percent_off}% off`
                              : coupon.amount_off
                                ? `$${(coupon.amount_off / 100).toFixed(2)} off`
                                : "Discount"}
                          </span>
                          {plan && (
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${plan === "Premium" ? "bg-amber-500/15 text-amber-700 dark:text-amber-400" : "bg-accent text-accent-foreground"}`}>
                              {plan} only
                            </span>
                          )}
                          {!coupon.valid && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-destructive/10 text-destructive font-medium">
                              Expired
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-0.5">
                          Used <span className="font-semibold text-foreground">{coupon.times_redeemed}</span>
                          {coupon.max_redemptions != null ? (
                            <> / {coupon.max_redemptions} times · <span className={`font-semibold ${remaining === 0 ? "text-destructive" : "text-primary"}`}>{remaining} remaining</span></>
                          ) : (
                            <> times · <span className="text-muted-foreground">Unlimited uses</span></>
                          )}
                          {" · "}{coupon.duration === "once" ? "One-time" : coupon.duration === "forever" ? "Forever" : coupon.duration}
                        </p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setDeletingId(coupon.id)} className="text-destructive hover:text-destructive">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}

          <AlertDialog open={!!deletingId} onOpenChange={(open) => !open && setDeletingId(null)}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Coupon</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete this coupon from Stripe. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => deletingId && deleteCoupon(deletingId)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AdminCoupons;
